import React, { useRef, useEffect, useState } from 'react';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  Settings, 
  Users, 
  Radio,
  Eye,
  MessageSquare,
  Heart,
  Share2
} from 'lucide-react';

interface LiveStreamData {
  title: string;
  stream: MediaStream;
  source: 'camera' | 'screen';
}

interface LiveStreamProps {
  streamUrl?: string;
  isLive?: boolean;
  streamData?: LiveStreamData | null;
}

interface QualityOption {
  label: string;
  value: string;
  resolution: string;
}

const LiveStream: React.FC<LiveStreamProps> = ({ 
  streamUrl = 'https://demo.unified-streaming.com/k8s/features/stable/video/tears-of-steel/tears-of-steel.ism/.m3u8',
  isLive = true,
  streamData
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [showSettings, setShowSettings] = useState(false);
  const [currentQuality, setCurrentQuality] = useState('720p');
  const [viewerCount, setViewerCount] = useState(Math.floor(Math.random() * 1500) + 100);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const qualityOptions: QualityOption[] = [
    { label: '1080p60', value: '1080p60', resolution: '1920x1080 60fps' },
    { label: '1080p', value: '1080p', resolution: '1920x1080' },
    { label: '720p60', value: '720p60', resolution: '1280x720 60fps' },
    { label: '720p', value: '720p', resolution: '1280x720' },
    { label: '480p', value: '480p', resolution: '854x480' },
    { label: '360p', value: '360p', resolution: '640x360' },
    { label: 'Auto', value: 'auto', resolution: 'Adaptive Quality' }
  ];

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // If we have live stream data from admin panel, use it
    if (streamData && streamData.stream) {
      video.srcObject = streamData.stream;
      video.play().catch(console.error);
      setIsLoading(false);
    }

    const handleLoadedData = () => {
      setIsLoading(false);
      setDuration(video.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    video.addEventListener('loadeddata', handleLoadedData);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);

    // Simulate viewer count updates
    const viewerInterval = setInterval(() => {
      setViewerCount(prev => prev + Math.floor(Math.random() * 10) - 5);
    }, 5000);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      clearInterval(viewerInterval);
    };
  }, [streamData]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = !video.muted;
    setIsMuted(video.muted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const handleQualityChange = (quality: string) => {
    setCurrentQuality(quality);
    setShowSettings(false);
    // In a real implementation, you would switch video sources here
    console.log('Quality changed to:', quality);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;

    const newTime = parseFloat(e.target.value);
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // Auto-hide controls
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    
    const resetTimeout = () => {
      clearTimeout(timeout);
      setShowControls(true);
      timeout = setTimeout(() => {
        if (isPlaying && !showSettings) {
          setShowControls(false);
        }
      }, 3000);
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('mousemove', resetTimeout);
      container.addEventListener('mouseenter', resetTimeout);
    }

    return () => {
      clearTimeout(timeout);
      if (container) {
        container.removeEventListener('mousemove', resetTimeout);
        container.removeEventListener('mouseenter', resetTimeout);
      }
    };
  }, [isPlaying, showSettings]);

  return (
    <div 
      ref={containerRef}
      className={`live-stream-container ${isFullscreen ? 'fullscreen' : ''}`}
    >
      {/* Live Badge */}
      {isLive && (
        <div className="live-badge">
          <Radio size={12} className="live-icon" />
          <span>LIVE</span>
          <div className="live-dot"></div>
        </div>
      )}

      {/* Viewer Count */}
      <div className="viewer-count">
        <Eye size={14} />
        <span>{viewerCount.toLocaleString()}</span>
      </div>

      {/* Video Element */}
      {!isLive || (!streamData && !streamUrl) ? (
        <div className="stream-offline">
          <Radio size={64} className="offline-icon" />
          <h2>Stream Offline</h2>
          <p>No live stream is currently available.</p>
          <p>The admin needs to start streaming first.</p>
        </div>
      ) : (
        <video
          ref={videoRef}
          className="live-video"
          poster="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='800' height='450'%3E%3Crect width='100%25' height='100%25' fill='%23000'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial' font-size='24' fill='%23fff' text-anchor='middle' dominant-baseline='middle'%3ELive Stream%3C/text%3E%3C/svg%3E"
          playsInline
        >
          {!streamData && <source src={streamUrl} type="application/x-mpegURL" />}
          Your browser does not support the video tag.
        </video>
      )}

      {/* Loading Spinner */}
      {isLoading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
        </div>
      )}

      {/* Controls */}
      <div className={`video-controls ${showControls ? 'visible' : ''}`}>
        {/* Progress Bar */}
        {!isLive && (
          <div className="progress-container">
            <input
              type="range"
              className="progress-bar"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
            />
          </div>
        )}

        <div className="controls-bar">
          {/* Play/Pause */}
          <button className="control-btn" onClick={togglePlay}>
            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
          </button>

          {/* Volume Controls */}
          <div className="volume-controls">
            <button className="control-btn" onClick={toggleMute}>
              {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
            </button>
            <input
              type="range"
              className="volume-slider"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
            />
          </div>

          {/* Time Display */}
          {!isLive && (
            <div className="time-display">
              <span>{formatTime(currentTime)} / {formatTime(duration)}</span>
            </div>
          )}

          <div className="controls-spacer"></div>

          {/* Chat & Social Actions */}
          <button className="control-btn">
            <MessageSquare size={18} />
          </button>
          <button className="control-btn">
            <Heart size={18} />
          </button>
          <button className="control-btn">
            <Share2 size={18} />
          </button>

          {/* Settings */}
          <div className="settings-container">
            <button 
              className="control-btn" 
              onClick={() => setShowSettings(!showSettings)}
            >
              <Settings size={18} />
            </button>
            
            {showSettings && (
              <div className="settings-menu">
                <div className="settings-section">
                  <h4>Quality</h4>
                  {qualityOptions.map((option) => (
                    <button
                      key={option.value}
                      className={`quality-option ${currentQuality === option.value ? 'active' : ''}`}
                      onClick={() => handleQualityChange(option.value)}
                    >
                      <span className="quality-label">{option.label}</span>
                      <span className="quality-resolution">{option.resolution}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Fullscreen */}
          <button className="control-btn" onClick={toggleFullscreen}>
            <Maximize size={18} />
          </button>
        </div>
      </div>

      {/* Chat Overlay (for live streams) */}
      {isLive && (
        <div className="chat-overlay">
          <div className="chat-header">
            <Users size={16} />
            <span>Live Chat</span>
          </div>
          <div className="chat-messages">
            <div className="chat-message">
              <span className="username">StreamViewer123:</span>
              <span className="message">Great stream! 🔥</span>
            </div>
            <div className="chat-message">
              <span className="username">TechFan99:</span>
              <span className="message">Amazing quality!</span>
            </div>
            <div className="chat-message">
              <span className="username">LiveWatcher:</span>
              <span className="message">Keep it up! 👍</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveStream; 